import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../config/axios';
import i18n, { SUPPORTED_LOCALES } from '../i18n';
import { normalizeLocale, resolveUiLanguageChoice } from '../utils/localeUtils';
import { useAuth } from './AuthContext';

const UserContext = createContext();

const DEFAULT_TIMEZONE = 'America/Sao_Paulo';
const DEFAULT_LOCALE = 'pt-BR';

/** LPs internacionais (/lp/:type/:country) mandam no idioma da URL; o perfil não sobrescreve. */
export function isCampaignLandingPath(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
  return pathname === '/lp' || pathname.startsWith('/lp/');
}

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    // Se não estiver dentro do provider, retorna valores padrão
    // Isso permite usar o hook mesmo fora do contexto (ex: páginas de auth)
    return {
      userProfile: null,
      profileMissing: false,
      userTimezone: DEFAULT_TIMEZONE,
      userLocale: DEFAULT_LOCALE,
      plan: null,
      planFeatures: null,
      projectLimit: null,
      loading: false,
      refreshProfile: () => {},
      updateLocale: async () => {},
    };
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  // true só quando a API respondeu e não existe linha em profiles (cadastro Google incompleto).
  const [profileMissing, setProfileMissing] = useState(false);
  const [userTimezone, setUserTimezone] = useState(DEFAULT_TIMEZONE);
  const [userLocale, setUserLocale] = useState(DEFAULT_LOCALE);
  const [plan, setPlan] = useState(null);
  const [planFeatures, setPlanFeatures] = useState(null);
  const [projectLimit, setProjectLimit] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearProfileState = useCallback(() => {
    setUserProfile(null);
    setProfileMissing(false);
    setUserTimezone(DEFAULT_TIMEZONE);
    setUserLocale(DEFAULT_LOCALE);
    setPlan(null);
    setPlanFeatures(null);
    setProjectLimit(null);
  }, []);

  // Buscar perfil do usuário (uma única GET /api/user/profile/ por boot)
  const fetchUserProfile = useCallback(async () => {
    if (!user) {
      clearProfileState();
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/api/user/profile/');
      const data = response.data || {};

      setPlan(data.plan || 'free');
      setPlanFeatures(data.plan_features || null);
      setProjectLimit(data.project_limit || null);

      setProfileMissing(Boolean(data.success) && !data.profile);
      if (data.profile) {
        const profile = data.profile;
        setUserProfile(profile);

        if (profile.timezone) {
          setUserTimezone(profile.timezone);
        }
        if (profile.preferred_locale) {
          const profileLocale = normalizeLocale(profile.preferred_locale);
          setUserLocale(profileLocale);
          // A UI só muda para um bundle que existe. Língua sem tradução (ja-JP)
          // fica no perfil e a interface usa inglês, sem regravar o banco.
          // Em /lp/* o país da URL define o idioma (ex.: ge → ka); não aplicar o perfil.
          const uiLocale = SUPPORTED_LOCALES.includes(profileLocale)
            ? profileLocale
            : resolveUiLanguageChoice(profileLocale);
          if (
            !isCampaignLandingPath() &&
            normalizeLocale(i18n.language) !== normalizeLocale(uiLocale)
          ) {
            i18n.changeLanguage(uiLocale);
          }
        }
      } else {
        setUserProfile(null);
      }
    } catch (error) {
      console.error('❌ UserContext: Erro ao buscar perfil:', error);
      setPlan('free');
      setPlanFeatures(null);
      setProjectLimit(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, clearProfileState]); // eslint-disable-line react-hooks/exhaustive-deps -- user.id evita loop se a referência mudar

  // Reage ao AuthContext (fonte única de usuário) — sem getUser() nem onAuthStateChange próprio
  useEffect(() => {
    if (authLoading) return;
    fetchUserProfile();
  }, [authLoading, fetchUserProfile]);

  const refreshProfile = useCallback(() => {
    return fetchUserProfile();
  }, [fetchUserProfile]);

  const updateLocale = useCallback(async (newLocale) => {
    const locale = normalizeLocale(newLocale);
    setUserLocale(locale);
    i18n.changeLanguage(locale);

    try {
      // locale-settings/ também sincroniza billing_preferences (moeda do checkout).
      await api.post('/api/user/profile/locale-settings/', { preferred_locale: locale });
    } catch (err) {
      console.error('❌ UserContext: Erro ao salvar locale:', err);
    }
  }, []);

  const value = {
    userProfile,
    profileMissing,
    userTimezone,
    userLocale,
    plan,
    planFeatures,
    projectLimit,
    loading: authLoading || loading,
    refreshProfile,
    updateLocale,
    DEFAULT_TIMEZONE,
    DEFAULT_LOCALE,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
